import React, { createContext, useContext, useState, ReactNode } from 'react';
import { WizardData, ExcelProductionData } from '../types/wizard';

/**
 * Initial wizard data state
 */
const initialWizardData: WizardData = {
    // Step 0: Cadastro management
    cadastro: undefined,

    // Step 1: Production file upload + Vendor quotas
    producao_planejada: undefined,
    cota_vendedores: [],
    cota_diaria: [],

    // Step 2: Client limits
    limite_clientes: [],

    savedStep: undefined,
};

/**
 * Wizard context type definition
 */
interface WizardContextType {
    data: WizardData;
    setData: React.Dispatch<React.SetStateAction<WizardData>>;
    updateCadastro: (cadastro: WizardData['cadastro']) => void;
    updateVendedorQuotas: (quotas: WizardData['cota_vendedores']) => void;
    updateDailyQuotas: (quotas: WizardData['cota_diaria']) => void;
    updateClienteLimits: (limits: WizardData['limite_clientes']) => void;
    updateProductionFile: (file: File | undefined) => void;
    updateExcelProductionData: (data: ExcelProductionData | undefined) => void;
    resetWizard: () => void;
}

/**
 * Wizard context for managing wizard state across components
 */
export const WizardContext = createContext<WizardContextType>({
    data: initialWizardData,
    setData: () => { },
    updateCadastro: () => { },
    updateVendedorQuotas: () => { },
    updateDailyQuotas: () => { },
    updateClienteLimits: () => { },
    updateProductionFile: () => { },
    updateExcelProductionData: () => { },
    resetWizard: () => { },
});

/**
 * Hook to access wizard context
 */
export const useWizard = () => {
    const context = useContext(WizardContext);
    if (!context) {
        throw new Error('useWizard must be used within a WizardProvider');
    }
    return context;
};

/**
 * Wizard provider component props
 */
interface WizardProviderProps {
    children: ReactNode;
}

/**
 * Wizard provider component that wraps the application
 */
export const WizardProvider: React.FC<WizardProviderProps> = ({ children }) => {
    const [data, setData] = useState<WizardData>(initialWizardData);

    /**
     * Update cadastro in wizard data
     */
    const updateCadastro = (cadastro: WizardData['cadastro']) => {
        setData(prev => ({ ...prev, cadastro: cadastro }));
    };

    /**
     * Update vendedor quotas in wizard data
     */
    const updateVendedorQuotas = (quotas: WizardData['cota_vendedores']) => {
        setData(prev => ({ ...prev, cota_vendedores: quotas }));
    };

    /**
     * Update daily quotas in wizard data
     */
    const updateDailyQuotas = (quotas: WizardData['cota_diaria']) => {
        setData(prev => ({ ...prev, cota_diaria: quotas }));
    };

    /**
     * Update cliente limits in wizard data
     */
    const updateClienteLimits = (limits: WizardData['limite_clientes']) => {
        setData(prev => ({ ...prev, limite_clientes: limits }));
    };

    /**
     * Update production file in wizard data
     */
    const updateProductionFile = (file: File | undefined) => {
        setData(prev => ({ ...prev, producao_planejada: file }));
    };

    /**
     * Update Excel production data in wizard data
     */
    const updateExcelProductionData = (data: ExcelProductionData | undefined) => {
        setData(prev => ({ ...prev, excel_production_data: data }));
    };

    /**
     * Reset wizard to initial state
     */
    const resetWizard = () => {
        setData(initialWizardData);
    };

    return (
        <WizardContext.Provider
            value={{
                data,
                setData,
                updateCadastro,
                updateVendedorQuotas,
                updateDailyQuotas,
                updateClienteLimits,
                updateProductionFile,
                updateExcelProductionData,
                resetWizard,
            }}
        >
            {children}
        </WizardContext.Provider>
    );
}; 